/**
 * Metafield filters (News / Bestsellers) for the collection grid.
 * Only News / only Bestsellers: match that metafield. Both on: product must be News AND Bestseller.
 * When filters are on and the collection spans multiple pages, remaining pages are
 * fetched via the Section Rendering API so the filter applies to the full catalog.
 */
import { sectionRenderer } from '@theme/section-renderer';

const PARAM_NEWS = 'rw_news';
const PARAM_BESTSELLERS = 'rw_bestsellers';
const FILTER_UPDATE_EVENT = 'filter:update';

/** @type {Promise<void> | null} */
let fetchAllPagesPromise = null;

function readUrlState() {
  const params = new URLSearchParams(window.location.search);
  return {
    news: params.get(PARAM_NEWS) === '1',
    bestsellers: params.get(PARAM_BESTSELLERS) === '1',
  };
}

function getPageNumberFromUrl() {
  const p = new URLSearchParams(window.location.search).get('page');
  const n = parseInt(p || '1', 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/**
 * Client-side filters only see the current page; reset to page 1 when filters are in the URL.
 */
function redirectDropPageForActiveFilters() {
  const params = new URLSearchParams(window.location.search);
  const page = getPageNumberFromUrl();
  const hasFilterInUrl =
    params.get(PARAM_NEWS) === '1' || params.get(PARAM_BESTSELLERS) === '1';
  if (!hasFilterInUrl || page <= 1) return false;

  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  window.location.replace(url.toString());
  return true;
}

function redirectWithFiltersAndResetPage(news, bestsellers) {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  if (news) {
    url.searchParams.set(PARAM_NEWS, '1');
  } else {
    url.searchParams.delete(PARAM_NEWS);
  }
  if (bestsellers) {
    url.searchParams.set(PARAM_BESTSELLERS, '1');
  } else {
    url.searchParams.delete(PARAM_BESTSELLERS);
  }
  window.location.assign(url.toString());
}

function writeUrlState(news, bestsellers) {
  const url = new URL(window.location.href);
  if (news) {
    url.searchParams.set(PARAM_NEWS, '1');
  } else {
    url.searchParams.delete(PARAM_NEWS);
  }
  if (bestsellers) {
    url.searchParams.set(PARAM_BESTSELLERS, '1');
  } else {
    url.searchParams.delete(PARAM_BESTSELLERS);
  }
  window.history.replaceState({}, '', url.toString());
}

/**
 * @param {string} html
 * @returns {Element | null}
 */
function findProductGridInSectionHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (
    doc.querySelector('#ResultsList .product-grid') ||
    doc.querySelector('ul.product-grid[ref="grid"]') ||
    doc.querySelector('ul.product-grid')
  );
}

/**
 * @param {HTMLElement} root
 * @param {boolean} loading
 */
function setMetafiltersLoading(root, loading) {
  const wrap = root.closest('[data-rootwell-metafilters-wrap]');
  const overlay = wrap?.querySelector('[data-rootwell-metafilters-loading]');
  if (!(overlay instanceof HTMLElement)) return;

  overlay.hidden = !loading;
  overlay.setAttribute('aria-hidden', loading ? 'false' : 'true');
  if (loading) {
    root.setAttribute('aria-busy', 'true');
  } else {
    root.removeAttribute('aria-busy');
  }
}

/**
 * @param {string} sectionId
 * @param {HTMLElement} grid
 * @param {HTMLElement} root
 */
async function ensureAllPagesLoaded(sectionId, grid, root) {
  if (grid.dataset.rwFullCatalog === '1') return;

  const totalPages = Number(grid.dataset.lastPage || '1');
  if (totalPages <= 1) {
    grid.dataset.rwFullCatalog = '1';
    return;
  }

  if (fetchAllPagesPromise) {
    await fetchAllPagesPromise;
    return;
  }

  if (!grid.dataset.rwOriginalLastPage) {
    grid.dataset.rwOriginalLastPage = String(totalPages);
  }

  fetchAllPagesPromise = (async () => {
    setMetafiltersLoading(root, true);
    try {
      const existingIds = new Set(
        Array.from(grid.querySelectorAll(':scope > .product-grid__item'))
          .map((li) => li.dataset.productId)
          .filter(Boolean)
      );

      for (let page = 2; page <= totalPages; page++) {
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(page));
        url.hash = '';

        const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
        const remoteGrid = findProductGridInSectionHtml(html);
        if (!remoteGrid) continue;

        remoteGrid.querySelectorAll(':scope > .product-grid__item').forEach((li) => {
          const pid = li.dataset.productId;
          if (!pid || existingIds.has(pid)) return;
          existingIds.add(pid);
          const clone = li.cloneNode(true);
          clone.setAttribute('data-rw-appended', '1');
          grid.appendChild(clone);
        });
      }

      grid.dataset.lastPage = '1';
      grid.dataset.rwFullCatalog = '1';
      document.dispatchEvent(new Event(FILTER_UPDATE_EVENT, { bubbles: true }));
    } finally {
      setMetafiltersLoading(root, false);
      fetchAllPagesPromise = null;
    }
  })();

  await fetchAllPagesPromise;
}

/**
 * Collection grid can use RootWell nav or theme .pagination; hide both when metafilters are active.
 * @param {HTMLElement} grid
 * @param {boolean} filtersActive
 */
function setPaginationVisibility(grid, filtersActive) {
  const resultsList = document.getElementById('ResultsList');
  const navs = resultsList
    ? resultsList.querySelectorAll('[data-rootwell-pagination], nav.pagination')
    : [];

  const lastPageNum = Number(grid.dataset.lastPage || '1');
  const hasMultiplePages = lastPageNum > 1;

  navs.forEach((nav) => {
    if (filtersActive) {
      nav.setAttribute('data-rw-suppress', '');
      nav.hidden = true;
      nav.setAttribute('aria-hidden', 'true');
    } else {
      nav.removeAttribute('data-rw-suppress');
      nav.removeAttribute('aria-hidden');
      nav.hidden = !hasMultiplePages;
    }
  });
}

/**
 * @param {HTMLElement} root
 */
async function apply(root) {
  const grid = document.querySelector('#ResultsList .product-grid');
  const empty = document.querySelector('[data-rootwell-metafilters-empty]');
  if (!grid) return;

  const newsInput = root.querySelector('[data-rw-filter="news"]');
  const bsInput = root.querySelector('[data-rw-filter="bestsellers"]');
  if (!newsInput || !bsInput) return;

  const news = newsInput.checked;
  const bestsellers = bsInput.checked;

  if (news || bestsellers) {
    setPaginationVisibility(grid, true);
  }

  if (!news && !bestsellers) {
    grid.querySelectorAll('.product-grid__item[data-rw-appended="1"]').forEach((el) => el.remove());
    if (grid.dataset.rwOriginalLastPage) {
      grid.dataset.lastPage = grid.dataset.rwOriginalLastPage;
      delete grid.dataset.rwOriginalLastPage;
    }
    delete grid.dataset.rwFullCatalog;
    document.dispatchEvent(new Event(FILTER_UPDATE_EVENT, { bubbles: true }));
  } else if (grid.dataset.rwFullCatalog !== '1') {
    const lastPage = Number(grid.dataset.lastPage || '1');
    if (lastPage <= 1) {
      grid.dataset.rwFullCatalog = '1';
    } else {
      const sectionEl = document.querySelector('results-list[section-id]');
      const sectionId = sectionEl?.getAttribute('section-id');
      if (sectionId) {
        await ensureAllPagesLoaded(sectionId, grid, root);
      } else {
        grid.dataset.rwFullCatalog = '1';
      }
    }
  }

  const items = grid.querySelectorAll(':scope > .product-grid__item');
  let visible = 0;

  items.forEach((li) => {
    const isNew = li.dataset.rwNewProduct === 'true';
    const isBs = li.dataset.rwBestseller === 'true';

    let show = true;
    if (news || bestsellers) {
      if (news && bestsellers) {
        show = isNew && isBs;
      } else if (news) {
        show = isNew;
      } else {
        show = isBs;
      }
    }

    li.hidden = !show;
    if (show) visible += 1;
  });

  if (empty) {
    empty.hidden = visible > 0;
  }

  setPaginationVisibility(grid, Boolean(news || bestsellers));

  writeUrlState(news, bestsellers);
}

function boot() {
  const root = document.querySelector('[data-rootwell-metafilters]');
  if (!root) return;

  if (redirectDropPageForActiveFilters()) return;

  const state = readUrlState();
  const newsInput = root.querySelector('[data-rw-filter="news"]');
  const bsInput = root.querySelector('[data-rw-filter="bestsellers"]');
  if (newsInput) newsInput.checked = state.news;
  if (bsInput) bsInput.checked = state.bestsellers;

  const run = () => {
    void apply(root);
  };
  run();

  root.addEventListener('change', (e) => {
    if (!e.target.matches('input[type="checkbox"][data-rw-filter]')) return;

    const newsInput = root.querySelector('[data-rw-filter="news"]');
    const bsInput = root.querySelector('[data-rw-filter="bestsellers"]');
    if (!newsInput || !bsInput) return;

    const news = newsInput.checked;
    const bestsellers = bsInput.checked;
    if ((news || bestsellers) && getPageNumberFromUrl() > 1) {
      redirectWithFiltersAndResetPage(news, bestsellers);
      return;
    }

    void apply(root);
  });

  const grid = document.querySelector('#ResultsList .product-grid');
  if (grid && typeof MutationObserver !== 'undefined') {
    let t = 0;
    const mo = new MutationObserver(() => {
      window.clearTimeout(t);
      t = window.setTimeout(run, 50);
    });
    mo.observe(grid, { childList: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
